import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Edit2, Trash2, Save, X, Code, Eye, ArrowLeft, Send, Copy } from 'lucide-react';
import { EmailTemplate, InterspireSegment, TemplateKind, normalizeTemplateKind } from '../types';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { splitHtmlDocument, reassembleHtml, handlePlainTextPaste, restorePlaceholderHrefs } from '../utils/htmlDocument';

const MAX_PARTS = 10;

type FormPart = { subject: string; htmlBody: string };

type EditorForm = {
  name: string;
  defaultSegmentId: number | null;
  kind: TemplateKind;
  parts: FormPart[];
};

const emptyPart = (): FormPart => ({ subject: '', htmlBody: '' });

const emptyForm = (): EditorForm => ({
  name: '',
  defaultSegmentId: null,
  kind: 'standard',
  parts: [emptyPart()],
});

function normalizeForCompare(form: EditorForm): string {
  return JSON.stringify({
    name: form.name,
    kind: form.kind,
    defaultSegmentId: form.defaultSegmentId,
    parts: form.parts.map((p) => ({
      subject: p.subject,
      body: splitHtmlDocument(p.htmlBody).bodyContent,
    })),
  });
}

/** Allow href="{{LINK}}" (and other {{PLACEHOLDER}} URIs) through DOMPurify. */
const TEMPLATE_URI_REGEXP =
  /^(?:\{\{[A-Z0-9_]+\}\}|#|(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$)))/i;

function sanitizeTemplateHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_URI_REGEXP: TEMPLATE_URI_REGEXP,
    ADD_ATTR: ['data-zoom-section'],
  });
}

function htmlHasZoomSection(html: string, key: string): boolean {
  return new RegExp(`data-zoom-section\\s*=\\s*["']${key}["']`, 'i').test(html);
}

function unwrapZoomSectionHtml(html: string, key: string): string | null {
  const openRe = new RegExp(
    `<(div|section|table)\\b([^>]*\\bdata-zoom-section\\s*=\\s*["']${key}["'][^>]*)>`,
    'i'
  );
  const m = openRe.exec(html);
  if (!m) return null;
  const tag = m[1];
  const start = m.index;
  const afterOpen = m.index + m[0].length;
  const closeRe = new RegExp(`</${tag}\\s*>`, 'gi');
  const openInnerRe = new RegExp(`<${tag}\\b[^>]*>`, 'gi');
  let depth = 1;
  let i = afterOpen;
  while (i < html.length && depth > 0) {
    openInnerRe.lastIndex = i;
    closeRe.lastIndex = i;
    const open = openInnerRe.exec(html);
    const close = closeRe.exec(html);
    if (!close) return null;
    if (open && open.index < close.index) {
      depth += 1;
      i = open.index + open[0].length;
    } else {
      depth -= 1;
      i = close.index + close[0].length;
      if (depth === 0) {
        return html.slice(0, start) + html.slice(afterOpen, close.index) + html.slice(i);
      }
    }
  }
  return null;
}

function partsFromTemplate(template: EmailTemplate): FormPart[] {
  const subjects = template.subjectLines?.length ? template.subjectLines : [''];
  const bodies =
    template.htmlBodies?.length > 0 ? template.htmlBodies : [template.htmlBody || ''];
  const n = Math.max(1, Math.min(MAX_PARTS, Math.max(subjects.length, bodies.length)));
  const parts: FormPart[] = [];
  for (let i = 0; i < n; i++) {
    parts.push({
      subject: subjects[i] ?? '',
      htmlBody: bodies[i] ?? bodies[0] ?? template.htmlBody ?? '',
    });
  }
  while (parts.length > 1 && !parts[parts.length - 1].subject.trim() && !parts[parts.length - 1].htmlBody.trim()) {
    parts.pop();
  }
  return parts.length ? parts : [emptyPart()];
}

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [segments, setSegments] = useState<InterspireSegment[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [htmlMode, setHtmlMode] = useState<'visual' | 'code'>('visual');
  const [activePart, setActivePart] = useState(0);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [templateToClone, setTemplateToClone] = useState<EmailTemplate | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [form, setForm] = useState<EditorForm>(emptyForm());
  const visualEditorRef = useRef<HTMLDivElement>(null);
  const [savedKey, setSavedKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [previewEmails, setPreviewEmails] = useState<string[]>([]);
  const [testRecipient, setTestRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const currentPart = form.parts[activePart] ?? form.parts[0];
  const isDirty = useMemo(() => normalizeForCompare(form) !== savedKey, [form, savedKey]);

  useEffect(() => {
    fetchTemplates();
    fetchSegments();
    axios
      .get<string[]>('/api/settings/preview-emails')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setPreviewEmails(list);
        setTestRecipient((prev) => prev || list[0] || '');
      })
      .catch(() => {});
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchSegments = async () => {
    try {
      const response = await axios.get('/api/interspire/segments');
      setSegments(response.data);
    } catch (error) {
      console.error('Error fetching segments:', error);
      setSegments([]);
    }
  };

  const flushVisualToPart = (partIndex: number, parts: FormPart[]): FormPart[] => {
    if (htmlMode !== 'visual' || !visualEditorRef.current) return parts;
    const next = parts.map((p) => ({ ...p }));
    const html = next[partIndex]?.htmlBody ?? '';
    const doc = splitHtmlDocument(html);
    next[partIndex] = {
      ...next[partIndex],
      htmlBody: reassembleHtml(doc, restorePlaceholderHrefs(visualEditorRef.current.innerHTML)),
    };
    return next;
  };

  const loadPartIntoVisual = (html: string) => {
    if (!visualEditorRef.current) return;
    const { bodyContent } = splitHtmlDocument(html);
    visualEditorRef.current.innerHTML = sanitizeTemplateHtml(bodyContent);
  };

  useEffect(() => {
    if (!isCreating || htmlMode !== 'visual') return;
    loadPartIntoVisual(form.parts[activePart]?.htmlBody ?? '');
    // Reload when entering visual or switching emails — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlMode, isCreating, activePart]);

  const patchPart = (index: number, patch: Partial<FormPart>) => {
    setForm((prev) => {
      const parts = prev.parts.map((p, i) => (i === index ? { ...p, ...patch } : p));
      return { ...prev, parts };
    });
  };

  const rangeInside = (el: HTMLElement): Range | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    const node = range.commonAncestorContainer;
    if (node === el || el.contains(node)) return range;
    return null;
  };

  const placeCaretAfter = (node: Node, editor: HTMLElement) => {
    editor.focus();
    const after = document.createRange();
    after.setStartAfter(node);
    after.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(after);
  };

  const insertPlaceholder = (placeholder: string) => {
    const token = `{{${placeholder}}}`;
    const html = currentPart?.htmlBody ?? '';
    const isLink = placeholder === 'LINK';

    if (htmlMode === 'visual' && visualEditorRef.current) {
      const editor = visualEditorRef.current;
      const range = rangeInside(editor);
      let inserted: Node;
      if (isLink) {
        const a = document.createElement('a');
        a.setAttribute('href', '{{LINK}}');
        if (range && !range.collapsed) {
          a.appendChild(range.extractContents());
          if (!a.textContent?.trim()) a.textContent = '{{LINK}}';
        } else {
          a.textContent = '{{LINK}}';
        }
        if (range) range.insertNode(a);
        else editor.appendChild(a);
        inserted = a;
      } else {
        const textNode = document.createTextNode(token);
        if (range) {
          range.deleteContents();
          range.insertNode(textNode);
        } else {
          editor.appendChild(textNode);
        }
        inserted = textNode;
      }
      placeCaretAfter(inserted, editor);
      const doc = splitHtmlDocument(html);
      patchPart(activePart, { htmlBody: reassembleHtml(doc, restorePlaceholderHrefs(editor.innerHTML)) });
      return;
    }

    const textArea = document.getElementById('htmlBodyCode') as HTMLTextAreaElement | null;
    const start = textArea ? textArea.selectionStart ?? html.length : html.length;
    const end = textArea ? textArea.selectionEnd ?? start : start;
    const selected = html.substring(start, end);
    const snippet = isLink ? `<a href="{{LINK}}">${selected || '{{LINK}}'}</a>` : token;
    if (!textArea) {
      patchPart(activePart, { htmlBody: html + snippet });
      return;
    }
    const next = html.substring(0, start) + snippet + html.substring(end);
    patchPart(activePart, { htmlBody: next });
    setTimeout(() => {
      textArea.focus();
      const pos = start + snippet.length;
      textArea.selectionStart = textArea.selectionEnd = pos;
    }, 0);
  };

  const wrapZoomSection = (key: 'writing' | 'management' | 'subawards') => {
    const html = currentPart?.htmlBody ?? '';
    const listToken =
      key === 'writing'
        ? '{{ZOOM_WRITING_LIST}}'
        : key === 'management'
          ? '{{ZOOM_MANAGEMENT_LIST}}'
          : '{{ZOOM_SUB_LIST}}';

    if (htmlMode === 'visual' && visualEditorRef.current) {
      const editor = visualEditorRef.current;
      const existing = editor.querySelector(`[data-zoom-section="${key}"]`);
      if (existing) {
        const parent = existing.parentNode;
        if (parent) {
          while (existing.firstChild) parent.insertBefore(existing.firstChild, existing);
          parent.removeChild(existing);
        }
        const doc = splitHtmlDocument(html);
        patchPart(activePart, { htmlBody: reassembleHtml(doc, restorePlaceholderHrefs(editor.innerHTML)) });
        return;
      }
      const range = rangeInside(editor);
      if (!range || range.collapsed) {
        window.alert(
          `Select the heading, intro, ZOOM WEBINARS line, and ${listToken}, then click Wrap. That whole block will hide when there are no matching Zoom classes.`
        );
        return;
      }
      const wrap = document.createElement('div');
      wrap.setAttribute('data-zoom-section', key);
      wrap.appendChild(range.extractContents());
      range.insertNode(wrap);
      placeCaretAfter(wrap, editor);
      const doc = splitHtmlDocument(html);
      patchPart(activePart, { htmlBody: reassembleHtml(doc, restorePlaceholderHrefs(editor.innerHTML)) });
      return;
    }

    if (htmlHasZoomSection(html, key)) {
      const next = unwrapZoomSectionHtml(html, key);
      if (next != null) patchPart(activePart, { htmlBody: next });
      return;
    }

    const textArea = document.getElementById('htmlBodyCode') as HTMLTextAreaElement | null;
    const start = textArea ? textArea.selectionStart ?? html.length : html.length;
    const end = textArea ? textArea.selectionEnd ?? start : start;
    if (start === end) {
      window.alert(
        `Select the heading, intro, ZOOM WEBINARS line, and ${listToken} in Code view, then click Wrap.`
      );
      return;
    }
    const selected = html.substring(start, end);
    const snippet = `<div data-zoom-section="${key}">\n${selected}\n</div>`;
    if (!textArea) {
      patchPart(activePart, { htmlBody: html + snippet });
      return;
    }
    const next = html.substring(0, start) + snippet + html.substring(end);
    patchPart(activePart, { htmlBody: next });
    setTimeout(() => {
      textArea.focus();
      textArea.selectionStart = start;
      textArea.selectionEnd = start + snippet.length;
    }, 0);
  };

  const handleVisualEditorInput = () => {
    if (visualEditorRef.current) {
      const doc = splitHtmlDocument(currentPart?.htmlBody ?? '');
      const full = reassembleHtml(doc, restorePlaceholderHrefs(visualEditorRef.current.innerHTML));
      patchPart(activePart, { htmlBody: full });
    }
  };

  const handleHtmlBodyChange = (content: string) => {
    patchPart(activePart, { htmlBody: content });
  };

  const handleModeSwitch = (mode: 'visual' | 'code') => {
    if (mode === htmlMode) return;
    if (mode === 'code' && htmlMode === 'visual') {
      setForm((prev) => ({ ...prev, parts: flushVisualToPart(activePart, prev.parts) }));
    }
    setHtmlMode(mode);
    if (mode === 'visual') {
      const html = form.parts[activePart]?.htmlBody ?? '';
      window.setTimeout(() => loadPartIntoVisual(html), 0);
    }
  };

  const selectPart = (index: number) => {
    if (index === activePart) return;
    setForm((prev) => {
      const parts = flushVisualToPart(activePart, prev.parts);
      setTimeout(() => loadPartIntoVisual(parts[index]?.htmlBody ?? ''), 0);
      return { ...prev, parts };
    });
    setActivePart(index);
  };

  const addPart = () => {
    if (form.parts.length >= MAX_PARTS) return;
    setForm((prev) => {
      if (prev.parts.length >= MAX_PARTS) return prev;
      const parts = [...flushVisualToPart(activePart, prev.parts), emptyPart()];
      const nextIndex = parts.length - 1;
      setTimeout(() => {
        setActivePart(nextIndex);
        loadPartIntoVisual('');
      }, 0);
      return { ...prev, parts };
    });
  };

  const removePart = (index: number) => {
    if (form.parts.length <= 1) return;
    setForm((prev) => {
      const flushed = flushVisualToPart(activePart, prev.parts);
      const parts = flushed.filter((_, i) => i !== index);
      const nextIndex = Math.min(index, parts.length - 1);
      setTimeout(() => {
        setActivePart(nextIndex);
        loadPartIntoVisual(parts[nextIndex]?.htmlBody ?? '');
      }, 0);
      return { ...prev, parts };
    });
  };

  const handleSaveTemplate = async () => {
    if (!isDirty || isSaving) return;
    try {
      const parts = flushVisualToPart(activePart, form.parts);
      const subjectLines = parts.map((p) => p.subject);
      const htmlBodies = parts.map((p) => p.htmlBody);
      if (!form.name.trim()) {
        window.alert('Enter a template name.');
        return;
      }
      if (!htmlBodies.some((h) => h.trim())) {
        window.alert('At least one email needs HTML content.');
        return;
      }
      const emptyHtmlParts = parts
        .map((p, i) => (!p.htmlBody.trim() && p.subject.trim() ? i + 1 : null))
        .filter((n): n is number => n != null);
      if (emptyHtmlParts.length > 0) {
        const ok = window.confirm(
          `Email ${emptyHtmlParts.join(', ')} have a subject but no HTML. Save anyway? Send Test will reuse the previous email’s HTML or send a placeholder.`
        );
        if (!ok) return;
      }
      const templateData = {
        name: form.name.trim(),
        kind: form.kind,
        defaultSegmentId: form.defaultSegmentId,
        htmlBody: htmlBodies[0] ?? '',
        htmlBodies,
        subjectLines,
      };

      setIsSaving(true);
      let saved: EmailTemplate;
      if (editingTemplate) {
        const res = await axios.put<EmailTemplate>(`/api/templates/${editingTemplate.id}`, templateData);
        saved = res.data;
      } else {
        const res = await axios.post<EmailTemplate>('/api/templates', templateData);
        saved = res.data;
      }

      const nextForm: EditorForm = {
        name: saved.name,
        defaultSegmentId: saved.defaultSegmentId ?? null,
        kind: normalizeTemplateKind(saved.kind),
        parts: partsFromTemplate(saved),
      };
      setForm(nextForm);
      setEditingTemplate(saved);
      setSavedKey(normalizeForCompare(nextForm));
      setActivePart((i) => Math.min(i, Math.max(0, nextForm.parts.length - 1)));
      void fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      window.alert('Failed to save template.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTest = async () => {
    const to = testRecipient.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      window.alert('Enter a valid test recipient email address.');
      return;
    }
    const parts = flushVisualToPart(activePart, form.parts);
    setForm((prev) => ({ ...prev, parts }));
    const part = parts[activePart] ?? parts[0];
    const body = part?.htmlBody?.trim() ?? '';
    if (!body) {
      window.alert(`Email ${activePart + 1} has no HTML to send.`);
      return;
    }
    const html = `${body}\n<!-- emailcraft-test ${Date.now()} -->`;
    const label =
      form.parts.length > 1
        ? `${form.name.trim() || 'Template'} · email ${activePart + 1}`
        : form.name.trim() || 'Template';
    const subject = `[TEST] ${label} — ${part.subject || 'No subject'}`.slice(0, 500);
    setSendingTest(true);
    try {
      await axios.post('/api/email/preview', { to, subject, html }, { timeout: 60_000 });
      window.alert(`Sent test of ${label} to ${to}.`);
    } catch (error) {
      console.error('Error sending test email:', error);
      const detail = axios.isAxiosError(error)
        ? error.response?.data?.error || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error';
      window.alert(`Failed to send test email: ${detail}`);
    } finally {
      setSendingTest(false);
    }
  };

  const openClone = (template: EmailTemplate) => {
    setTemplateToClone(template);
    setCloneName(`${template.name} (copy)`);
  };

  const handleCloneTemplate = async () => {
    if (!templateToClone || isCloning) return;
    const name = cloneName.trim();
    if (!name) {
      window.alert('Enter a name for the cloned template.');
      return;
    }
    if (name === templateToClone.name.trim()) {
      window.alert('Give the clone a different name than the original.');
      return;
    }
    if (isCreating && isDirty) {
      const ok = window.confirm(
        'You have unsaved template edits. Clone anyway and switch to the new copy?'
      );
      if (!ok) return;
    }
    setIsCloning(true);
    try {
      const source = templateToClone;
      const htmlBodies =
        source.htmlBodies?.length > 0 ? source.htmlBodies : [source.htmlBody || ''];
      const subjectLines = source.subjectLines?.length ? source.subjectLines : [''];
      const res = await axios.post<EmailTemplate>('/api/templates', {
        name,
        kind: normalizeTemplateKind(source.kind),
        defaultSegmentId: source.defaultSegmentId ?? null,
        htmlBody: htmlBodies[0] ?? '',
        htmlBodies,
        subjectLines,
      });
      setTemplateToClone(null);
      await fetchTemplates();
      startEditing(res.data);
    } catch (error) {
      console.error('Error cloning template:', error);
      window.alert('Failed to clone template.');
    } finally {
      setIsCloning(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/api/templates/${templateToDelete.id}`);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      window.alert('Failed to delete template.');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    const next = emptyForm();
    setForm(next);
    setSavedKey('');
    setActivePart(0);
    setIsCreating(false);
    setEditingTemplate(null);
    setHtmlMode('visual');
    setIsSaving(false);
    if (visualEditorRef.current) {
      visualEditorRef.current.innerHTML = '';
    }
  };

  const startNewTemplate = () => {
    const next = emptyForm();
    setForm(next);
    setSavedKey(normalizeForCompare(next));
    setActivePart(0);
    setEditingTemplate(null);
    setHtmlMode('visual');
    setIsCreating(true);
    window.setTimeout(() => loadPartIntoVisual(''), 0);
  };

  const startEditing = (template: EmailTemplate) => {
    const parts = partsFromTemplate(template);
    const next: EditorForm = {
      name: template.name,
      defaultSegmentId: template.defaultSegmentId ?? null,
      kind: normalizeTemplateKind(template.kind),
      parts,
    };
    setEditingTemplate(template);
    setForm(next);
    setSavedKey(normalizeForCompare(next));
    setActivePart(0);
    setIsCreating(true);
    setHtmlMode('visual');
    setTimeout(() => loadPartIntoVisual(parts[0]?.htmlBody ?? ''), 0);
  };

  const eventPlaceholders = [
    'LOCATION',
    'DATE',
    'INTRO',
    'LINK',
    'DISCOUNT',
    'EXTRA_INFO',
    'CONTACT_1',
    'CONTACT_2',
    'CONTACT_3',
  ];
  const zoomPlaceholders = [
    'ZOOM_WRITING_LIST',
    'ZOOM_MANAGEMENT_LIST',
    'ZOOM_SUB_LIST',
    'ZOOM_SPOTLIGHT',
  ];
  const fixedPlaceholders =
    form.kind === 'zoom' ? [...eventPlaceholders, ...zoomPlaceholders] : eventPlaceholders;

  const partCount = (t: EmailTemplate) =>
    Math.max(1, t.htmlBodies?.length || t.subjectLines?.filter((s) => s.trim()).length || 1);

  return (
    <div className="px-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Email Templates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create one email or a sequence (each part has its own subject and HTML). Placeholders like{' '}
            {`{{LOCATION}}`} work in every part.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex flex-wrap gap-2 justify-end">
          {isCreating && (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              View Templates
            </button>
          )}
          <button
            type="button"
            onClick={startNewTemplate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </button>
        </div>
      </div>

      {isCreating && (
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </h3>

            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Template Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Enter template name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="kind" className="block text-sm font-medium text-gray-700">
                    Kind
                  </label>
                  <select
                    id="kind"
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value as TemplateKind })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="standard">Standard</option>
                    <option value="alumni">Alumni</option>
                    <option value="combo">Combo</option>
                    <option value="zoom">Zoom</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {form.kind === 'zoom'
                      ? 'Used only by Build → Zoom Emails. Wrap writing/management/subawards blocks with Hide if empty (each kept section gets a top horizontal rule). Insert {{ZOOM_SPOTLIGHT}} where the optional message/graphic should appear (unused tags stay invisible; spotlight also gets a top rule only).'
                      : 'Label only.'}
                  </p>
                </div>
                <div>
                  <label htmlFor="defaultSegmentId" className="block text-sm font-medium text-gray-700">
                    Default segment
                  </label>
                  <select
                    id="defaultSegmentId"
                    value={form.defaultSegmentId ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        defaultSegmentId: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">None</option>
                    {segments.map((s) => (
                      <option key={s.segmentid} value={s.segmentid}>
                        {s.segmentname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Emails in sequence ({form.parts.length}/{MAX_PARTS})
                  </p>
                  <button
                    type="button"
                    onClick={addPart}
                    disabled={form.parts.length >= MAX_PARTS}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add email
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.parts.map((_, index) => (
                    <div key={index} className="inline-flex items-center">
                      <button
                        type="button"
                        onClick={() => selectPart(index)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                          activePart === index
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Email {index + 1}
                      </button>
                      {form.parts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePart(index)}
                          className="ml-0.5 p-1 text-gray-400 hover:text-red-600"
                          title="Remove this email"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subject {activePart + 1}
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Use {`{{LOCATION}}`} and {`{{DATE}}`} only in subjects.
                </p>
                <input
                  type="text"
                  value={currentPart?.subject ?? ''}
                  onChange={(e) => patchPart(activePart, { subject: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder={`Subject for email ${activePart + 1}`}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    HTML — email {activePart + 1}
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('visual')}
                      className={`px-3 py-1 text-sm font-medium rounded-l-md border ${
                        htmlMode === 'visual'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Eye className="h-4 w-4 inline mr-1" />
                      Visual
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('code')}
                      className={`px-3 py-1 text-sm font-medium rounded-r-md border-t border-r border-b ${
                        htmlMode === 'code'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Code className="h-4 w-4 inline mr-1" />
                      Code
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-700 mb-2">Quick Insert Placeholders:</div>
                  <div className="flex flex-wrap gap-2">
                    {fixedPlaceholders.map((placeholder) => (
                      <button
                        key={placeholder}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertPlaceholder(placeholder)}
                        title={
                          placeholder === 'LINK'
                            ? 'Insert as a clickable HTML link (href={{LINK}})'
                            : `Insert {{${placeholder}}}`
                        }
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        {placeholder}
                      </button>
                    ))}
                  </div>
                  {form.kind === 'zoom' && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">
                        Hide whole block if no Zoom classes
                      </div>
                      <p className="text-[11px] text-gray-500 mb-1.5">
                        Select the title, intro, ZOOM WEBINARS line, and the matching list placeholder, then wrap it.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(
                          [
                            ['writing', 'Writing'],
                            ['management', 'Management'],
                            ['subawards', 'Subawards'],
                          ] as const
                        ).map(([key, label]) => {
                          const wrapped = htmlHasZoomSection(currentPart?.htmlBody ?? '', key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => wrapZoomSection(key)}
                              title={
                                wrapped
                                  ? `Remove the ${label} hide-if-empty wrap`
                                  : `Wrap the selected ${label} block so it hides when that list is empty`
                              }
                              className={`inline-flex items-center px-2 py-1 border shadow-sm text-xs font-medium rounded ${
                                wrapped
                                  ? 'border-cyan-500 text-cyan-900 bg-cyan-100 hover:bg-cyan-200'
                                  : 'border-cyan-300 text-cyan-800 bg-cyan-50 hover:bg-cyan-100'
                              }`}
                            >
                              {wrapped ? `Unwrap ${label}` : `Wrap ${label}`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {htmlMode === 'visual' ? null : (
                  <textarea
                    id="htmlBodyCode"
                    rows={15}
                    value={currentPart?.htmlBody ?? ''}
                    onChange={(e) => handleHtmlBodyChange(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
                    placeholder="Enter HTML content. Use {{PLACEHOLDER}} for dynamic content."
                  />
                )}
                <div
                  ref={visualEditorRef}
                  contentEditable
                  className={`min-h-[300px] block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-4 border focus:outline-none zoom-visual-editor ${
                    htmlMode === 'visual' ? '' : 'hidden'
                  }`}
                  style={{ minHeight: '300px' }}
                  onInput={handleVisualEditorInput}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                />

                <p className="mt-1 text-xs text-gray-500">
                  Available placeholders: {fixedPlaceholders.map((p) => `{{${p}}}`).join(', ')}
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSendTest()}
                    disabled={sendingTest}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title={`Send a test of email ${activePart + 1} only`}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sendingTest ? 'Sending…' : `Send Test (email ${activePart + 1})`}
                  </button>
                  <input
                    type="email"
                    list="template-test-recipients"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder="Test recipient email"
                    autoComplete="email"
                    className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500 min-w-[14rem]"
                  />
                  <datalist id="template-test-recipients">
                    {previewEmails.map((email) => (
                      <option key={email} value={email} />
                    ))}
                  </datalist>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveTemplate()}
                    disabled={!isDirty || isSaving}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      !isDirty || isSaving
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving…' : isDirty ? 'Save Template' : 'Saved'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Template
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kind
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Emails
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Default segment
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Placeholders
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template) => {
              const allHtml = (template.htmlBodies?.length ? template.htmlBodies : [template.htmlBody]).join(
                '\n'
              );
              const placeholders = [...eventPlaceholders, ...zoomPlaceholders].filter((p) =>
                allHtml.includes(`{{${p}}}`)
              );

              return (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        template.kind === 'alumni'
                          ? 'bg-blue-100 text-blue-800'
                          : template.kind === 'combo'
                            ? 'bg-purple-100 text-purple-800'
                            : template.kind === 'zoom'
                              ? 'bg-cyan-100 text-cyan-800'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {normalizeTemplateKind(template.kind)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {partCount(template)} {partCount(template) === 1 ? 'email' : 'emails'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.defaultSegmentId
                      ? (segments.find((s) => s.segmentid === template.defaultSegmentId)?.segmentname ??
                        `#${template.defaultSegmentId}`)
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {placeholders.map((placeholder) => (
                        <span
                          key={placeholder}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                        >
                          {placeholder}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(template.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => startEditing(template)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                      title="Edit template"
                    >
                      <Edit2 className="h-4 w-4 inline" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openClone(template)}
                      className="text-gray-600 hover:text-blue-700 mr-4"
                      title="Clone template"
                    >
                      <Copy className="h-4 w-4 inline" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplateToDelete(template)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {templates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No templates created yet.</p>
          </div>
        )}
      </div>

      {templateToClone && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 dark:bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clone-template-title"
        >
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md p-5 border border-gray-200 dark:border-slate-700">
            <h3 id="clone-template-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Clone template
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
              Copy{' '}
              <span className="font-medium text-gray-900 dark:text-white">“{templateToClone.name}”</span>
              {partCount(templateToClone) > 1
                ? ` (${partCount(templateToClone)} emails in the sequence)`
                : ''}
              . Give the copy a new name, then you can change the wording without affecting the original.
            </p>
            <label htmlFor="clone-template-name" className="block mt-4 text-sm font-medium text-gray-700 dark:text-slate-200">
              New name
            </label>
            <input
              id="clone-template-name"
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCloneTemplate();
                }
              }}
              autoFocus
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !isCloning && setTemplateToClone(null)}
                disabled={isCloning}
                className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCloneTemplate()}
                disabled={isCloning}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                  isCloning ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isCloning ? 'Cloning…' : 'Clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {templateToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 dark:bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-title"
        >
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md p-5 border border-gray-200 dark:border-slate-700">
            <h3 id="delete-template-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete template?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
              This will permanently delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">“{templateToDelete.name}”</span>
              {partCount(templateToDelete) > 1
                ? ` (${partCount(templateToDelete)} emails in the sequence)`
                : ''}
              . This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !isDeleting && setTemplateToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteTemplate()}
                disabled={isDeleting}
                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                  isDeleting ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
